import React from "react";
import { animated, useSpring } from "react-spring";
import useDimensions from "../../hooks/useDimensions";
import styles from "./personalisation-options.module.scss";
import { IconPencil } from "../icons";
import PERSONALISATION_DATA from "../../data/personalisation.json";

export const DEMOGRAPHIC_ROWS = [
    {
        dataKey: "age",
        question: "How old are you?",
        values: ["15-17", "18-24", "25-34", "35-49", "50-64", "65 and over"],
        labels: ["15-17", "18-24", "25-34", "35-49", "50-64", "65 and over"],
    },
    {
        dataKey: "study",
        question: "Are you currently studying?",
        values: ["Secondary school", "University", "TAFE", "Other institution", "Not studying"],
        labels: [
            "Studying at secondary school",
            "Studying at university",
            "Studying at TAFE",
            "Studying at another institution",
            "Not currently studying",
        ],
    },
    {
        dataKey: "employ",
        question: "What is your employment status?",
        values: [
            "Full-time",
            "Part-time",
            "Casual",
            "Contract / freelance",
            "Volunteer",
            "Not employed",
        ],
        labels: [
            "Full-time employment",
            "Part-time employment",
            "Casual employment",
            "Contract / freelance employment",
            "Volunteering",
            "Not currently employed",
        ],
    },
    {
        dataKey: "finance",
        question: "What is your financial situation?",
        values: [
            "Very good",
            "Pretty good",
            "Neither good nor bad",
            "Pretty bad",
            "Very bad",
            "I don't know",
        ],
        labels: [
            "Very good financial situation",
            "Pretty good financial situation",
            "Neither good nor bad financial situation",
            "Pretty bad financial situation",
            "Very bad financial situation",
            "Not sure about financial situation",
        ],
    },
];

const EMPTY_VALUES = DEMOGRAPHIC_ROWS.reduce((acc, n) => ({ ...acc, [n.dataKey]: null }), {});

const PersonalisationOptions = props => {
    const { isOpen, onOptionsSubmitted, onEditClick } = props;
    const expandedRef = React.useRef(null);
    const collapsedRef = React.useRef(null);
    const expandedDimensions = useDimensions(expandedRef);
    const collapsedDimensions = useDimensions(collapsedRef);

    const [values, setValues] = React.useState(EMPTY_VALUES);
    const handleValueChange = React.useCallback((key, value) => {
        setValues(v => ({
            ...v,
            [key]: v[key] === value ? null : value,
        }));
    }, []);

    const rows = DEMOGRAPHIC_ROWS.map(row => {
        return (
            <PersonalisationRow
                {...row}
                key={row.dataKey}
                onValueChange={handleValueChange}
                currentValue={values[row.dataKey]}
                currentOptions={values}
            />
        );
    });

    const height = isOpen
        ? (expandedDimensions && expandedDimensions.height) || 0
        : (collapsedDimensions && collapsedDimensions.height) || 0;

    const accordionSpring = useSpring({
        height,
    });

    const expandedContainerSpring = useSpring({
        opacity: isOpen ? 1 : 0,
        pointerEvents: isOpen ? "all" : "none",
    });

    const expandedButtonSpring = useSpring({
        opacity: isOpen ? 1 : 0,
        pointerEvents: isOpen ? "all" : "none",
        bottom: `calc(-${height}px)`,
    });

    const collapsedContainerSpring = useSpring({
        opacity: isOpen ? 0 : 1,
        pointerEvents: isOpen ? "none" : "all",
    });

    const headerMarginSpring = useSpring({
        marginBottom: isOpen ? 0 : (collapsedDimensions && collapsedDimensions.height + 24) || 0,
    });

    const collapsedButtons = Object.entries(values)
        .filter(kvp => kvp[1] !== null)
        .map(kvp => {
            const [key, value] = kvp;
            const label = DEMOGRAPHIC_ROWS.find(d => d.dataKey === key).labels[value];

            return (
                <div
                    key={label}
                    className={styles.valueButton}
                    data-selected={true}
                    data-interactive="false"
                    onClick={() => onEditClick()}
                >
                    {label}
                </div>
            );
        });

    return (
        <div className={styles.personalisationOptions}>
            <animated.div className={styles.headerBlock} style={headerMarginSpring}>
                <div className={styles.heading}>
                    <span className={styles.em}>How's your</span> financial wellbeing?
                </div>
            </animated.div>
            <animated.div className={styles.optionsAccordion} style={accordionSpring}>
                <animated.div
                    ref={collapsedRef}
                    className={styles.collapsedContainer}
                    style={collapsedContainerSpring}
                >
                    <button className={styles.editButton} onClick={onEditClick}>
                        <IconPencil />{" "}
                        {collapsedButtons.length === 0 ? "Make it personal" : "Change who you are"}
                    </button>
                    {collapsedButtons.length > 0 && (
                        <div className={styles.collapsedButtons}>{collapsedButtons}</div>
                    )}
                </animated.div>
                <animated.div
                    ref={expandedRef}
                    className={styles.expandedContainer}
                    style={expandedContainerSpring}
                >
                    <div className={styles.expandedRows}>{rows}</div>
                </animated.div>
            </animated.div>
            <animated.div className={styles.submitButtons} style={expandedButtonSpring}>
                <button className={styles.compareButton} onClick={() => onOptionsSubmitted(values)}>
                    Compare with people like you
                </button>
                <button
                    className={styles.everyoneButton}
                    onClick={() => {
                        onOptionsSubmitted(null);
                        setValues(EMPTY_VALUES);
                    }}
                >
                    Or compare with everyone
                </button>
            </animated.div>
        </div>
    );
};

const getOptionsCount = (currentOptions, dataKey, valueIndex) => {
    const mergeOptions = {
        ...currentOptions,
        [dataKey]: valueIndex,
    };
    const filtered = PERSONALISATION_DATA.filter(row => {
        return Object.entries(mergeOptions).every(kvp => {
            const [valueKey, optionValue] = kvp;
            if (optionValue === null) return true;
            return row.demographics[valueKey] === optionValue;
        });
    });

    return filtered.length;
};

const PersonalisationRow = props => {
    const { dataKey, question, values, currentValue, onValueChange, currentOptions } = props;

    const buttons = values.map((value, valueIndex) => {
        const disabled = getOptionsCount(currentOptions, dataKey, valueIndex) === 0;
        const selected = valueIndex === currentValue;
        return (
            <button
                key={value}
                className={styles.valueButton + ` ${dataKey}-${valueIndex}`}
                data-selected={selected}
                disabled={disabled}
                onClick={() => onValueChange(dataKey, valueIndex)}
            >
                {value}
            </button>
        );
    });

    return (
        <div className={styles.row}>
            <div className={styles.questionText}>{question}</div>
            <div className={styles.buttons}>{buttons}</div>
        </div>
    );
};

export default PersonalisationOptions;
